import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTimeFrame } from '../contexts/TimeFrameContext';
import apiClient from '../utils/apiClient';
import AuthService from '../services/authService';
import { logger } from '../utils/logger';
import type { BranchSearchResult } from '../types/api';

export interface FilterBrand {
  id: number;
  name: string;
  logo?: string;
}

export interface FilterBranch {
  id: number;
  name: string;
  brandId: number;
  currency?: string;
  logo?: string;
  branchCloudConsumerId?: number;
}

export interface FilterPeriod {
  value: string;
  label: string;
}

export const PERIOD_OPTIONS: FilterPeriod[] = [
  { value: 'last1Month', label: '۱ ماه اخیر' },
  { value: 'last3Months', label: '۳ ماه اخیر' },
  { value: 'last6Months', label: '۶ ماه اخیر' },
  { value: 'lastYear', label: '۱۲ ماه اخیر' },
];

export function periodLabelForValue(value: string): string {
  return PERIOD_OPTIONS.find((p) => p.value === value)?.label ?? '۶ ماه اخیر';
}

function formatBranchSearchLabel(result: BranchSearchResult): string {
  const lockNumber = result.BranchCloudConsumerId;
  const lockSuffix = lockNumber != null ? ` (کد: ${lockNumber})` : '';
  return `${result.BranchName} — ${result.BrandName}${lockSuffix}`;
}

function formatBranchOptionLabel(branch: FilterBranch): string {
  if (branch.branchCloudConsumerId != null) {
    return `${branch.name} (کد: ${branch.branchCloudConsumerId})`;
  }
  return branch.name;
}

interface UseContextFiltersOptions {
  onBrandChange: (brand: FilterBrand | null) => void;
  onBranchChange: (branch: FilterBranch | null) => void;
  onPeriodChange: (period: string) => void;
}

export function useContextFilters({
  onBrandChange,
  onBranchChange,
  onPeriodChange,
}: UseContextFiltersOptions) {
  const { timeFrame, setTimeFrame } = useTimeFrame();

  const [brands, setBrands] = useState<FilterBrand[]>([]);
  const [branches, setBranches] = useState<FilterBranch[]>([]);
  const [selectedBrand, setSelectedBrand] = useState<FilterBrand | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<FilterBranch | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<FilterPeriod>(() => ({
    value: timeFrame,
    label: periodLabelForValue(timeFrame),
  }));
  const [loading, setLoading] = useState(false);
  const [branchSearchLoading, setBranchSearchLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [branchSearchResults, setBranchSearchResults] = useState<BranchSearchResult[]>([]);
  const [brandSearchQuery, setBrandSearchQuery] = useState('');

  const pendingBranchIdRef = useRef<number | null>(null);
  const searchAbortRef = useRef<AbortController | null>(null);
  const branchOptionMapRef = useRef<Map<string, BranchSearchResult>>(new Map());

  useEffect(() => {
    const fetchBrands = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get('/brands');
        const mappedBrands = response.data.map((brand: any) => ({
          id: brand.BrandID,
          name: brand.BrandName,
          logo: brand.LogoAddress,
        }));
        setBrands(mappedBrands);

        const savedBrandId = AuthService.getSavedBrandId();
        if (savedBrandId && mappedBrands.length > 0) {
          const savedBrand = mappedBrands.find((b: FilterBrand) => b.id === savedBrandId);
          if (savedBrand) {
            setSelectedBrand(savedBrand);
            onBrandChange(savedBrand);
            localStorage.setItem('insight_selected_brand_name', savedBrand.name);
            return;
          }
        }

        if (mappedBrands.length > 0) {
          const firstBrand = mappedBrands[0];
          setSelectedBrand(firstBrand);
          onBrandChange(firstBrand);
          AuthService.saveBrandSelection(firstBrand.id);
          localStorage.setItem('insight_selected_brand_name', firstBrand.name);
        }
      } catch (err) {
        logger.error('Error fetching brands', 'useContextFilters', err);
        setError('دریافت فهرست برندها ناموفق بود؛ لطفاً اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.');
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedBrand) {
      setBranches([]);
      setSelectedBranch(null);
      onBranchChange(null);
      return;
    }

    const fetchBranches = async () => {
      setLoading(true);
      try {
        const response = await apiClient.get(`/branches/brand/${selectedBrand.id}`);
        const mappedBranches: FilterBranch[] = response.data.map((branch: any) => ({
          id: branch.BranchID,
          name: branch.BranchName,
          brandId: branch.BrandId,
          currency: branch.Currency,
          logo: branch.LogoAddress || selectedBrand.logo,
          branchCloudConsumerId: branch.BranchCloudConsumerId ?? undefined,
        }));
        setBranches(mappedBranches);

        const pendingId = pendingBranchIdRef.current;
        if (pendingId) {
          const pending = mappedBranches.find((b) => b.id === pendingId);
          pendingBranchIdRef.current = null;
          if (pending) {
            setSelectedBranch(pending);
            onBranchChange(pending);
            AuthService.saveBranchSelection(pending.id);
            localStorage.setItem('insight_selected_branch_name', pending.name);
            return;
          }
        }

        const savedBranchId = AuthService.getSavedBranchId();
        if (savedBranchId && mappedBranches.length > 0) {
          const savedBranch = mappedBranches.find((b: FilterBranch) => b.id === savedBranchId);
          if (savedBranch) {
            setSelectedBranch(savedBranch);
            onBranchChange(savedBranch);
            localStorage.setItem('insight_selected_branch_name', savedBranch.name);
            return;
          }
        }

        if (mappedBranches.length > 0) {
          const firstBranch = mappedBranches[0];
          setSelectedBranch(firstBranch);
          onBranchChange(firstBranch);
          AuthService.saveBranchSelection(firstBranch.id);
          localStorage.setItem('insight_selected_branch_name', firstBranch.name);
        } else {
          setSelectedBranch(null);
          onBranchChange(null);
        }
      } catch (err) {
        logger.error('Error fetching branches', 'useContextFilters', err);
        setError('دریافت فهرست شعب ناموفق بود؛ لطفاً اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.');
      } finally {
        setLoading(false);
      }
    };

    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrand]);

  useEffect(() => {
    const trimmed = brandSearchQuery.trim();
    const isNumericSearch = /^\d+$/.test(trimmed) && trimmed.length >= 3;

    if (!isNumericSearch) {
      searchAbortRef.current?.abort();
      searchAbortRef.current = null;
      setBranchSearchResults([]);
      setBranchSearchLoading(false);
      return;
    }

    const timer = window.setTimeout(async () => {
      searchAbortRef.current?.abort();
      const controller = new AbortController();
      searchAbortRef.current = controller;

      setBranchSearchLoading(true);
      try {
        const response = await apiClient.get('/branches/search', {
          params: { q: trimmed },
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setBranchSearchResults(Array.isArray(response.data) ? response.data : []);
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        const isCanceled =
          (err as { code?: string; name?: string })?.code === 'ERR_CANCELED' ||
          (err as { name?: string })?.name === 'CanceledError';
        if (!isCanceled) {
          logger.error('Error searching branches by lockNumber', 'useContextFilters', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setBranchSearchLoading(false);
        }
      }
    }, 300);

    return () => {
      window.clearTimeout(timer);
    };
  }, [brandSearchQuery]);

  useEffect(() => {
    if (timeFrame !== selectedPeriod.value) {
      setSelectedPeriod({ value: timeFrame, label: periodLabelForValue(timeFrame) });
    }
  }, [timeFrame, selectedPeriod.value]);

  const handleBrandSearchChange = useCallback((query: string) => {
    setBrandSearchQuery(query);
  }, []);

  const handleBrandChange = useCallback(
    (option: { value: string | number; label: string } | null) => {
      if (!option) {
        pendingBranchIdRef.current = null;
        if (selectedBrand) {
          setSelectedBrand(null);
          onBrandChange(null);
        }
        return;
      }

      const valueAsString = String(option.value);
      if (valueAsString.startsWith('branch-')) {
        const branchResult = branchOptionMapRef.current.get(valueAsString);
        if (!branchResult) return;

        const brandFromList = brands.find((brand) => brand.id === branchResult.BrandId);
        const brandToSelect: FilterBrand = brandFromList ?? {
          id: branchResult.BrandId,
          name: branchResult.BrandName,
        };

        setBrandSearchQuery('');
        setBranchSearchResults([]);

        if (selectedBrand?.id === brandToSelect.id) {
          const existingBranch = branches.find((branch) => branch.id === branchResult.BranchID);
          const branchToSelect: FilterBranch = existingBranch ?? {
            id: branchResult.BranchID,
            name: branchResult.BranchName,
            brandId: branchResult.BrandId,
            currency: branchResult.Currency,
            branchCloudConsumerId: branchResult.BranchCloudConsumerId ?? undefined,
            logo: brandToSelect.logo,
          };
          setSelectedBranch(branchToSelect);
          onBranchChange(branchToSelect);
          AuthService.saveBranchSelection(branchToSelect.id);
          localStorage.setItem('insight_selected_branch_name', branchToSelect.name);
          return;
        }

        pendingBranchIdRef.current = branchResult.BranchID;
        setSelectedBrand(brandToSelect);
        onBrandChange(brandToSelect);
        AuthService.saveBrandSelection(brandToSelect.id);
        localStorage.setItem('insight_selected_brand_name', brandToSelect.name);
        return;
      }

      pendingBranchIdRef.current = null;
      const selected = brands.find((brand) => brand.id === Number(option.value));
      if (selected && (!selectedBrand || selectedBrand.id !== selected.id)) {
        setSelectedBrand(selected);
        onBrandChange(selected);
        AuthService.saveBrandSelection(selected.id);
        localStorage.setItem('insight_selected_brand_name', selected.name);
      }
    },
    [brands, branches, selectedBrand, onBrandChange, onBranchChange],
  );

  const handleBranchChange = useCallback(
    (option: { value: string | number; label: string } | null) => {
      if (!option) {
        if (selectedBranch) {
          setSelectedBranch(null);
          onBranchChange(null);
        }
        return;
      }
      const selected = branches.find((branch) => branch.id === Number(option.value));
      if (selected && (!selectedBranch || selectedBranch.id !== selected.id)) {
        setSelectedBranch(selected);
        onBranchChange(selected);
        AuthService.saveBranchSelection(selected.id);
        localStorage.setItem('insight_selected_branch_name', selected.name);
      }
    },
    [branches, selectedBranch, onBranchChange],
  );

  const handlePeriodChange = useCallback(
    (option: { value: string | number; label: string } | null) => {
      if (!option) return;
      const periodOption = PERIOD_OPTIONS.find((p) => p.value === option.value);
      if (periodOption && selectedPeriod.value !== periodOption.value) {
        setSelectedPeriod(periodOption);
        setTimeFrame(periodOption.value);
        onPeriodChange(periodOption.value);
      }
    },
    [selectedPeriod.value, setTimeFrame, onPeriodChange],
  );

  const clearError = useCallback(() => setError(null), []);

  const brandOptions = useMemo(() => {
    const isNumericSearch = /^\d+$/.test(brandSearchQuery.trim()) && brandSearchQuery.trim().length >= 3;
    const nextBranchOptionMap = new Map<string, BranchSearchResult>();

    const branchOptionsFromSearch = isNumericSearch
      ? branchSearchResults.map((result) => {
          const value = `branch-${result.BranchID}`;
          nextBranchOptionMap.set(value, result);
          return {
            value,
            label: formatBranchSearchLabel(result),
          };
        })
      : [];

    branchOptionMapRef.current = nextBranchOptionMap;

    const brandOptionsFromList = brands.map((brand) => ({
      value: brand.id,
      label: brand.name,
    }));

    return [...branchOptionsFromSearch, ...brandOptionsFromList];
  }, [brands, branchSearchResults, brandSearchQuery]);

  const branchOptions = useMemo(
    () =>
      branches.map((branch) => ({
        value: branch.id,
        label: formatBranchOptionLabel(branch),
      })),
    [branches],
  );

  return {
    brands,
    branches,
    selectedBrand,
    selectedBranch,
    selectedPeriod,
    loading,
    branchSearchLoading,
    error,
    clearError,
    handleBrandChange,
    handleBrandSearchChange,
    handleBranchChange,
    handlePeriodChange,
    brandOptions,
    branchOptions,
  };
}
